import { useMemo } from 'react';

import { FunctionIcon } from '@polyai/ui/components/atoms/Icons';
import { PopupMenuItem } from '@polyai/ui/components/molecules/PopupMenu';
import { INTEGRATION_LOGO_MAP } from '@polyai/ui/constants/integrations.constants';
import { parseIntegrationFunctionId } from '@polyai/ui/utils';

import { useIntegrationConfig } from 'api/queries/useIntegrationConfig';
import { INTEGRATION_PREFIX } from 'components/molecules/LexicalEditor/features/action/constants';
import type {
  ActionTagContentProps,
  ActionTypeInput,
  ActionTypeOutput,
} from 'components/molecules/LexicalEditor/features/action/types';
import { INTEGRATION_LIST } from 'screens/Integrations/constants';
import { useViewMode } from 'screens/withPermissions/useViewMode';

const IntegrationActionContent = ({
  id,
  name,
  remove,
  closePopup: _closePopup,
}: ActionTagContentProps) => {
  const { isEditable } = useViewMode();
  const { data: integrationConfig } = useIntegrationConfig();

  // Parse the ID format: "provider__function_name" or "provider__all"
  const { providerId, functionName } = parseIntegrationFunctionId(id);

  const provider = providerId
    ? integrationConfig?.providers.find(p => p.id === providerId)
    : null;

  // Handle "all" case
  if (functionName === 'all') {
    return (
      <>
        <PopupMenuItem isDisabled>
          <div className="flex flex-col gap-xs1">
            <div>{name}</div>
            <div className="text-sm opacity-70">
              Enables all {provider?.actions?.length || 0} available actions
            </div>
          </div>
        </PopupMenuItem>
        {isEditable && (
          <PopupMenuItem isDelete onClick={remove}>
            Remove
          </PopupMenuItem>
        )}
      </>
    );
  }

  const action = provider?.actions.find(a => a.function_name === functionName);

  if (!action) {
    return (
      <>
        {isEditable && (
          <PopupMenuItem isDelete onClick={remove}>
            Remove
          </PopupMenuItem>
        )}
      </>
    );
  }

  return (
    <>
      <PopupMenuItem isDisabled>
        <div className="flex flex-col gap-xs1">
          <div>{name}</div>
          <div className="text-sm opacity-70">{action.description}</div>
        </div>
      </PopupMenuItem>
      {isEditable && (
        <PopupMenuItem isDelete onClick={remove}>
          Remove
        </PopupMenuItem>
      )}
    </>
  );
};

export const useIntegrationActionType = ({
  id,
}: ActionTypeInput): ActionTypeOutput => {
  const { data: integrationConfig, isLoading } = useIntegrationConfig();

  const actionData = useMemo(() => {
    if (!integrationConfig || !id) {
      return null;
    }

    const { providerId, functionName } = parseIntegrationFunctionId(id);

    const provider = integrationConfig.providers.find(p => p.id === providerId);
    if (!provider) {
      return null;
    }

    const Logo =
      INTEGRATION_LOGO_MAP[providerId as keyof typeof INTEGRATION_LOGO_MAP];

    const Icon = () => (
      <span className="pr-xs1">{Logo ? <Logo /> : <FunctionIcon />}</span>
    );

    if (functionName.endsWith('__all')) {
      const integrationInfo = INTEGRATION_LIST.find(i => i.id === providerId);
      return {
        name: `All ${integrationInfo?.name || providerId} actions`,
        description: `Use all available ${integrationInfo?.name || providerId} actions`,
        Icon,
        provider: providerId,
      };
    }

    const action = provider.actions.find(a => a.function_name === functionName);
    if (!action) {
      return null;
    }

    return {
      name: action.display_name,
      description: action.description,
      Icon,
      provider: providerId,
    };
  }, [id, integrationConfig]);

  const warning =
    !isLoading && id && !actionData
      ? `Integration action not found: ${id}`
      : undefined;

  return {
    isEnabled: true,
    isLoading,
    name: actionData?.name || id || 'Unknown Integration',
    prefix: INTEGRATION_PREFIX,
    Content: IntegrationActionContent,
    Icon: actionData?.Icon || FunctionIcon,
    warning,
  };
};
